/**
 * `/sign-out`: platform-wide logout, run before the app boots.
 *
 * Three separate sessions can exist and all three have to go: this origin's
 * OIDC session in per-tab sessionStorage (shared with the v2 app), hub's
 * httpOnly cookie on hub's own origin, and Cognito's hosted SSO cookie. Only
 * the first is ours to clear directly. After clearing it, the browser is
 * handed to hub, which clears its cookie, revokes the tokens pool-wide
 * (GlobalSignOut) and performs the Cognito `/logout` redirect back to this
 * origin.
 *
 * `index.js` calls this instead of rendering React, and that ordering is the
 * fix. When `/sign-out` was a route inside the running app, the guard, the
 * navbar and every API call in flight were still mounted when the OIDC user
 * was removed. Each of them could ask for a token, find none, and start a
 * hidden `prompt=none` iframe or a full authorize redirect. Cognito's live SSO
 * cookie answered silently, so logout either restored the session or replaced
 * the hub navigation with `/v2/sign-in`, and the user landed back in the app.
 * With no app running there is nothing left to race.
 *
 * Deleting the oidc-client-ts keys directly is safe here for the same reason:
 * no UserManager has been created on this page, so there is no in-memory
 * state to desynchronise.
 *
 * `returnTo` is the bare origin on purpose. Hub forwards it to Cognito as
 * `logout_uri`, and Cognito only accepts registered sign-out URLs.
 */
import { buildHubLogoutUrl } from '../lib/hub';
import { clearLocalSessionState } from '../lib/localSession';

const SIGN_OUT_PATH = '/sign-out';

/** oidc-client-ts `WebStorageStateStore` default prefix (user and signin state). */
const OIDC_STORAGE_PREFIX = 'oidc.';

export function isSignOutPath(pathname = window.location.pathname) {
  return (pathname.replace(/\/+$/, '') || '/') === SIGN_OUT_PATH;
}

export function removeOidcSessionEntries(store = window.sessionStorage) {
  try {
    const keys = [];

    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);

      if (key && key.startsWith(OIDC_STORAGE_PREFIX)) {
        keys.push(key);
      }
    }

    keys.forEach((key) => store.removeItem(key));
  } catch {
    // Storage unavailable: nothing to clear.
  }
}

export function signOutAndLeave() {
  clearLocalSessionState();
  removeOidcSessionEntries();
  // `replace`, so Back does not land on `/sign-out` and log out again.
  window.location.replace(buildHubLogoutUrl());
}
